/**
 * ⭐⭐⭐ `R2-UI8` · **El borde de las respuestas automáticas de un brazo.**
 * `// ADR-0348`
 *
 * Las CUATRO rutas de `contracts/admin-api/http.ts` ⟦respuestas-automaticas⟧,
 * que `R2-CAA2` fijó y `R2-AW6` (`ADR-0347`) sirvió:
 *
 *   GET    brazos/{brazo}/respuestas-automaticas?pagina=N&tamano=M  → 200
 *   POST   brazos/{brazo}/respuestas-automaticas                    → 201
 *   PUT    brazos/{brazo}/respuestas-automaticas/{id}               → 200
 *   DELETE brazos/{brazo}/respuestas-automaticas/{id}               → 204
 *
 * ════════════════════════════════════════════════════════════════════════════
 * ⛔⛔ ① `ADR-0330` · LOS VALORES DEL CONTRATO NO SE IMPORTAN, Y EL HUECO SE
 *      CIERRA CON UN EXAMEN — NO CON UN COMENTARIO
 * ════════════════════════════════════════════════════════════════════════════
 *
 * El alias `@contracts/*` vive en `tsconfig.json` y **NO** en `vite.config.ts`,
 * a propósito: `import type` lo borra `esbuild` y nunca llega al bundler, pero un
 * VALOR importado de un contrato **compila con `tsc` y rompe `vite build`**. Es
 * el defecto que `ADR-0330` nombra y el que `R2-UI5` resolvió en
 * `login/borde-sesion.ts:84-87`.
 *
 * ⇒ acá se aplica la MISMA disciplina, en dos capas:
 *
 *   ① **los literales se escriben a mano y los sostiene un ALIAS DE TIPO**
 *      (`AutoRepliesPath`, `AutoReplyPath`): un typo en el segmento es un
 *      `error TS2322` y no un `404` en producción;
 *   ② **lo que un alias de tipo NO puede sostener —los NÚMEROS— lo ancla un
 *      examen que SÍ puede importar el valor**: `pruebas/r2-ui8-botones.test.tsx`
 *      §«el contrato y la pantalla cuentan lo mismo» importa
 *      `BUTTON_TEXT_MAX_CODEPOINTS`, `AUTO_REPLY_MAX_CODEPOINTS` y
 *      `countCodePoints` de `contracts/channel` y los compara contra los de acá.
 *
 * ⚠️ **Y la segunda capa no es una formalidad.** `MAX_TEXTO_DE_BOTON = 25`
 * escrito a mano compila igual valiendo `24`, `26` o `250`: ningún tipo del repo
 * puede decir que está mal. El examen es el único instrumento que puede.
 *
 * ════════════════════════════════════════════════════════════════════════════
 * ⛔⛔⛔ ② `25` PUNTOS DE CÓDIGO NO ES `25` UNIDADES UTF-16, Y EL ÚNICO CASO QUE
 *      LO DISCRIMINA ES EL DEL EMOJI
 * ════════════════════════════════════════════════════════════════════════════
 *
 * `contracts/channel` ⟦el-largo-se-cuenta-en-PUNTOS-DE-CODIGO⟧: `[...s].length`
 * itera por puntos de código; `.length` cuenta unidades UTF-16, y todo lo que
 * está fuera del BMP —un emoji, **y el equipo del estudio escribe botones con
 * emojis**— cuenta DOS.
 *
 * ⛔ **El atributo HTML `maxlength` cuenta unidades UTF-16.** Por eso el panel NO
 * lo usa como la regla: un botón de 24 letras más un emoji mide `25` puntos de
 * código —válido para el servidor— y `26` unidades UTF-16, así que `maxlength`
 * le comería el último carácter al operador. La regla la decide
 * `contarPuntosDeCodigo`, acá, con la misma forma que el contrato.
 *
 * ⚠️ **Un examen escrito sólo con texto ASCII PASA IGUAL con `.length`.** El
 * caso del emoji no es un extra: es lo único que separa las dos cuentas.
 *
 * ════════════════════════════════════════════════════════════════════════════
 * ⛔ ③ LO QUE ESTE BORDE **NO** HACE
 * ════════════════════════════════════════════════════════════════════════════
 *
 *   · **No calcula «Dónde aparece».** `apareceEn` llega CALCULADO por fila
 *     (`contracts/admin-api/index.ts` ⟦donde-aparece-se-calcula⟧, `ADR-0247`,
 *     `A10-2`): el servidor es el único que tiene las dos lecturas en la misma
 *     transacción. Cruzarlo en el navegador contra el catálogo de la otra
 *     pestaña normaliza distinto y usa dos instantes distintos.
 *   · **No normaliza el texto del botón.** `buttonText` va tal como el operador
 *     lo escribió (`DatosDeRespuestaAutomatica`): la forma normalizada la deriva
 *     `modules/channel`. Mandarlo normalizado sería dejar que el navegador elija
 *     la clave única.
 *   · **No manda `arm` en ningún cuerpo ni en ninguna query.** El brazo va SÓLO
 *     en la ruta (⟦respuestas-automaticas⟧: un `?arm=` es literalmente la
 *     primera petición manipulada que edita la respuesta de baja del otro brazo).
 *   · **No manda `actor`.** ⟦http-sesion⟧: lo resuelve `apps/admin-web` desde la
 *     cookie de sesión.
 *   · **No ordena y no pagina del lado del navegador.** Regla 19: el orden de
 *     creación lo aplica el PUERTO y el corte es `?pagina=&tamano=`.
 */

import type { Arm, AutoRepliesPath, AutoReplyPath } from "@contracts/admin-api/http.ts"
import type {
  AutoReplyAction,
  AutoReplyId,
  DatosDeRespuestaAutomatica,
  RespuestaAutomatica,
} from "@contracts/admin-api/index.ts"
import type { ConfiguracionInstancia } from "../../configuracion.ts"
import { mensajeDeError } from "../../mensaje-de-error.ts"

/**
 * ⭐ Las cuatro acciones, **en el orden de `PRD_R2.md:1114-1119`**, que es el
 * orden en que el panel las ofrece.
 *
 * ⛔ **El tipo lo ancla el contrato** (`AutoReplyAction`): el día que
 * `contracts/channel` agregue una quinta, esto sigue compilando pero el examen
 * `§«las cuatro acciones son las del contrato»` se pone rojo y alguien tiene que
 * contestar qué rótulo y qué ayuda le tocan. Sin ese examen, la opción nueva
 * simplemente no se ofrecería y nada lo diría.
 */
export const ACCIONES_EN_ORDEN: readonly AutoReplyAction[] = [
  "ninguna",
  "siguio_con_chatbot",
  "pidio_humano",
  "opt_out",
]

/**
 * ⛔ **La acción que sólo existe donde hay derivación** (regla 15,
 * `PRD_R2.md:1209`). Se nombra UNA vez y por su valor de contrato, **nunca
 * comparando el brazo contra un literal de brazo**: `gates/literales-de-brazo.spec.ts`
 * lo prohíbe y un `brazo === "puro"` ata la pantalla a una instancia concreta.
 */
export const ACCION_DE_DERIVACION: AutoReplyAction = "pidio_humano"

/** ⛔ La acción de baja. Decide los DOS avisos propios (`CA-32`, `CA-37`). */
export const ACCION_DE_BAJA: AutoReplyAction = "opt_out"

/** La acción elegida por defecto al abrir el panel (`CA-19`). */
export const ACCION_POR_DEFECTO: AutoReplyAction = "ninguna"

/**
 * **El largo máximo del texto del botón, en PUNTOS DE CÓDIGO.** Ver el bloque ①:
 * el literal se escribe a mano porque el valor no se puede importar, y lo ancla
 * el examen contra `BUTTON_TEXT_MAX_CODEPOINTS` del contrato.
 */
export const MAX_TEXTO_DE_BOTON = 25

/** El largo máximo de la respuesta, en PUNTOS DE CÓDIGO. Ancla: `AUTO_REPLY_MAX_CODEPOINTS`. */
export const MAX_RESPUESTA = 4096

/**
 * ⭐⭐ **Cuenta PUNTOS DE CÓDIGO, no unidades UTF-16.** Ver el bloque ②.
 *
 * ⛔ **`[...texto].length` y no `texto.length`.** Es la misma forma que
 * `countCodePoints` de `contracts/channel` y que `largoEnPuntosDeCodigo` de
 * `login/borde-sesion.ts`; que las tres coincidan lo mide el examen, no este
 * comentario.
 */
export function contarPuntosDeCodigo(texto: string): number {
  return [...texto].length
}

/** Una fila de la lista, tal como la pantalla la dibuja. Es la del contrato, sin traducir. */
export type FilaDeRespuesta = RespuestaAutomatica

/** Lo que el operador escribe. Los tres campos, completos, igual al crear y al editar. */
export type DatosDelPanel = DatosDeRespuestaAutomatica

/**
 * Una página de la lista.
 *
 * ⚠️ **`total` es el del BRAZO ENTERO, no el largo de `respuestas`.** Es lo que
 * `src/paginado.tsx` necesita para calcular M en «Página N de M», y pasarle
 * `respuestas.length` sería la mentira que esa primitiva declara en su encabezado.
 */
export type PaginaDeRespuestas = {
  readonly respuestas: readonly FilaDeRespuesta[]
  readonly total: number
}

/**
 * El borde visto desde el navegador. **Cuatro operaciones, ni una más.**
 *
 * ⛔ No hay `buscar`, no hay `reordenar` y no hay `dondeAparece`: las tres serían
 * caminos nuevos hacia la misma colección que el cable no declara.
 */
export type BordeBotones = {
  /** `GET …/respuestas-automaticas?pagina=&tamano=`. `pagina` es **base 1**. */
  readonly listar: (brazo: Arm, pagina: number, tamano: number) => Promise<PaginaDeRespuestas>
  /** `POST …/respuestas-automaticas`. `201` + la fila **con su `id` y su `apareceEn`**. */
  readonly crear: (brazo: Arm, datos: DatosDelPanel) => Promise<FilaDeRespuesta>
  /** `PUT …/respuestas-automaticas/{id}`. `200` + la fila resultante. */
  readonly editar: (brazo: Arm, id: AutoReplyId, datos: DatosDelPanel) => Promise<FilaDeRespuesta>
  /** `DELETE …/respuestas-automaticas/{id}`. `204`, **sin cuerpo**. */
  readonly eliminar: (brazo: Arm, id: AutoReplyId) => Promise<void>
}

/**
 * ⭐⭐ **El `409` tiene que llegar a la pantalla como un HECHO CON NOMBRE, no
 * como un texto.** `CA-17` y `CA-31`.
 *
 * La frase *«Ya existe una respuesta automática para «X» en KAI · Brazo Y. Edita
 * esa.»* **la arma la pantalla** con el texto que MANDÓ y el sello del brazo,
 * porque `ADR-0201` prohíbe que el cuerpo del error del servidor traiga valores
 * del pedido. ⇒ el borde no puede devolver el texto crudo del cuerpo y esperar
 * que sirva: lo único que puede decir es **cuál de los desenlaces fue**.
 *
 * ⛔ **Y por eso es una propiedad del error y no un `instanceof` de una clase
 * nueva:** el resto de la app ya distingue errores de borde por `statusCode`
 * (`mensaje-de-error.ts`), y una jerarquía propia acá sería un segundo
 * vocabulario para lo mismo.
 */
export type ErrorDelBorde = Error & { readonly statusCode: number }

/** `409 duplicated` de `AUTO_REPLIES_API_STATUS`. Escrito a mano, anclado por el examen. */
export const ESTADO_REPETIDA = 409

/**
 * ¿Este error es el `409` de texto de botón repetido?
 *
 * ⛔ Se lee el campo a través de `Record<string, unknown>` y **no con un
 * `as { statusCode: number }`**: ese `as` no compila (`TS2352`, «neither type
 * sufficiently overlaps») y el arreglo cómodo —`as unknown as …`— afirmaría la
 * forma en vez de comprobarla, que es justo lo que esta función existe para
 * hacer. Un error sin `statusCode` contesta `false`, que es lo correcto: un fallo
 * de red no es un `409`.
 */
export function esRepetida(causa: unknown): boolean {
  if (!(causa instanceof Error)) return false
  const codigo = (causa as unknown as Record<string, unknown>)["statusCode"]
  return codigo === ESTADO_REPETIDA
}

function errorDelBorde(mensaje: string, statusCode: number): ErrorDelBorde {
  return Object.assign(new Error(mensaje), { statusCode })
}

/*
 * El acuerdo de cable, todo junto. Ver el bloque ①: los valores se repiten acá y
 * los TIPOS son del contrato, así que una divergencia rompe el typecheck.
 *
 * ⚠️ El `{brazo}` y el `{id}` van percent-encoded. El `{id}` es un uuid
 * (⟦respuestas-automaticas⟧) **y aun así se codifica**: este borde no le pide al
 * lector que confíe en eso, por el mismo criterio que el `{docId}` del banco.
 */
const RUTA_BRAZOS = "brazos"
const SEGMENTO_RESPUESTAS = "respuestas-automaticas"
const CAMPO_PAGINA = "pagina"
const CAMPO_TAMANO = "tamano"

function rutaDeLaColeccion(brazo: Arm): AutoRepliesPath {
  return `${RUTA_BRAZOS}/${encodeURIComponent(brazo)}/${SEGMENTO_RESPUESTAS}`
}

function rutaDeUna(brazo: Arm, id: AutoReplyId): AutoReplyPath {
  return `${rutaDeLaColeccion(brazo)}/${encodeURIComponent(id)}`
}

function esObjeto(valor: unknown): valor is Record<string, unknown> {
  return typeof valor === "object" && valor !== null && !Array.isArray(valor)
}

/**
 * ⛔ **La acción se estrecha a las CUATRO del contrato y nada más.**
 *
 * Aceptar un quinto valor dejaría a la pantalla con una celda «Además de
 * responder» vacía y con un `<input type="radio">` que ninguna opción tiene
 * marcado — o sea, una fila que no se puede editar sin cambiarle la acción sin
 * querer. Que la respuesta no se entienda se DICE, como en los otros bordes.
 */
function leerAccion(valor: unknown): AutoReplyAction | null {
  return typeof valor === "string" && (ACCIONES_EN_ORDEN as readonly string[]).includes(valor)
    ? (valor as AutoReplyAction)
    : null
}

/**
 * Una fila del cable.
 *
 * ⛔⛔ **`apareceEn` ausente NO se rellena con `[]`.** `[]` significa «este botón
 * no está en ninguna plantilla del brazo» —un HECHO que la columna dice con una
 * frase propia (`CA-09`)— y un campo que no vino es una respuesta que no cumple
 * el contrato. Colapsarlos haría que un servidor roto le dijera al operador, con
 * todas las letras, que su botón no está en ninguna plantilla: exactamente el
 * error de tipeo que la columna existe para destapar, fabricado por el lector.
 * Es la misma dirección de falla que `lastSyncedAt` en `borde-plantillas.ts`.
 */
function leerFila(valor: unknown): FilaDeRespuesta | null {
  if (!esObjeto(valor)) return null

  const id = valor["id"]
  if (typeof id !== "string") return null

  const buttonText = valor["buttonText"]
  if (typeof buttonText !== "string") return null

  const replyText = valor["replyText"]
  if (typeof replyText !== "string") return null

  const action = leerAccion(valor["action"])
  if (action === null) return null

  const crudas = valor["apareceEn"]
  if (!Array.isArray(crudas)) return null
  const apareceEn: string[] = []
  for (const cruda of crudas) {
    if (typeof cruda !== "string") return null
    apareceEn.push(cruda)
  }

  const createdAt = valor["createdAt"]
  if (typeof createdAt !== "string") return null

  return { id, buttonText, replyText, action, apareceEn, createdAt }
}

/**
 * El sobre de la lectura: `respuestas` y `total`, **dos campos y dos hechos**.
 *
 * ⛔ **`total` ausente NO se rellena con `respuestas.length`.** Con una página de
 * 50 sobre un brazo de 120, `respuestas.length` daría `50` y el paginado
 * dibujaría «Página 1 de 1» escondiendo 70 filas, sin que nada se pusiera rojo.
 */
function leerPagina(valor: unknown): PaginaDeRespuestas | null {
  if (!esObjeto(valor)) return null

  const crudas = valor["respuestas"]
  if (!Array.isArray(crudas)) return null

  const respuestas: FilaDeRespuesta[] = []
  for (const cruda of crudas) {
    const fila = leerFila(cruda)
    if (fila === null) return null
    respuestas.push(fila)
  }

  const total = valor["total"]
  if (typeof total !== "number" || !Number.isFinite(total)) return null

  return { respuestas, total }
}

const NO_SE_ENTIENDE =
  "El servidor contestó las respuestas automáticas en una forma que no se entiende."

/**
 * Arma el borde contra la `admin-web` configurada.
 *
 * Función y no módulo con estado, por lo mismo que los otros bordes: la dirección
 * llega en tiempo de ejecución (`configuracion.json`) y no hay ningún momento de
 * import en el que conocerla.
 */
export function crearBordeBotonesHttp(config: ConfiguracionInstancia): BordeBotones {
  const raiz = config.adminWebUrl

  /**
   * `credentials: "include"` y ningún `Authorization` armado acá, igual que en
   * los otros bordes: la sesión es una cookie `HttpOnly` que el navegador manda
   * solo. Un token que el JavaScript tuviera que leer sería un token que esta
   * unidad tuvo que GUARDAR, y no guarda nada.
   */
  const pedir = async (ruta: string, init: RequestInit): Promise<Response> => {
    let respuesta: Response
    try {
      respuesta = await fetch(`${raiz}/${ruta}`, { ...init, credentials: "include" })
    } catch (causa) {
      throw Object.assign(errorDelBorde(`No se pudo hablar con el servidor (${raiz}).`, 0), {
        cause: causa,
      })
    }
    if (!respuesta.ok) {
      throw errorDelBorde(
        await mensajeDeError(respuesta, `El servidor respondió ${respuesta.status}.`),
        respuesta.status,
      )
    }
    return respuesta
  }

  const leerUna = async (respuesta: Response): Promise<FilaDeRespuesta> => {
    const fila = leerFila(await respuesta.json())
    if (fila === null) throw errorDelBorde(NO_SE_ENTIENDE, 502)
    return fila
  }

  const cuerpo = (datos: DatosDelPanel): string =>
    JSON.stringify({
      buttonText: datos.buttonText,
      replyText: datos.replyText,
      action: datos.action,
    })

  return {
    async listar(brazo: Arm, pagina: number, tamano: number): Promise<PaginaDeRespuestas> {
      const ruta: AutoRepliesPath = rutaDeLaColeccion(brazo)
      /*
       * ⚠️ La query se arma con `URLSearchParams` y no concatenando: los dos
       * valores son números, pero el día que uno venga de un campo escribible
       * (el «Ir a la página» del paginado) la concatenación es una inyección de
       * query.
       */
      const query = new URLSearchParams({
        [CAMPO_PAGINA]: String(pagina),
        [CAMPO_TAMANO]: String(tamano),
      })

      const respuesta = await pedir(`${ruta}?${query.toString()}`, {
        method: "GET",
        headers: { Accept: "application/json" },
      })

      const leida = leerPagina(await respuesta.json())
      if (leida === null) throw errorDelBorde(NO_SE_ENTIENDE, 502)
      return leida
    },

    async crear(brazo: Arm, datos: DatosDelPanel): Promise<FilaDeRespuesta> {
      const ruta: AutoRepliesPath = rutaDeLaColeccion(brazo)
      return leerUna(
        await pedir(ruta, {
          method: "POST",
          headers: { Accept: "application/json", "Content-Type": "application/json" },
          body: cuerpo(datos),
        }),
      )
    },

    async editar(brazo: Arm, id: AutoReplyId, datos: DatosDelPanel): Promise<FilaDeRespuesta> {
      const ruta: AutoReplyPath = rutaDeUna(brazo, id)
      /*
       * ⛔ `PUT` y no `PATCH`, con el MISMO cuerpo que el alta: RT-7 valida los
       * tres campos al crear **o** editar, así que un parcial permitiría expresar
       * un estado que el contrato dice que no existe (⟦ar-edicion⟧).
       */
      return leerUna(
        await pedir(ruta, {
          method: "PUT",
          headers: { Accept: "application/json", "Content-Type": "application/json" },
          body: cuerpo(datos),
        }),
      )
    },

    async eliminar(brazo: Arm, id: AutoReplyId): Promise<void> {
      const ruta: AutoReplyPath = rutaDeUna(brazo, id)
      /*
       * ⛔ `204` **sin cuerpo**: no se lee `.json()`. ⟦ar-baja⟧ decide que la fila
       * borrada NO vuelve al navegador —el puerto ya se la dio al llamador—, y
       * leer un cuerpo que el contrato dice que no existe dejaría a esta rama
       * dependiendo de que el servidor viole su propio contrato.
       */
      await pedir(ruta, { method: "DELETE", headers: { Accept: "application/json" } })
    },
  }
}
